/**
 * ReportsPanel — Salesperson Performance report.
 *
 * ONE row per salesperson within the selected date range, showing
 * Total Quotations, Total Value, Closed / Pending / Lost counts +
 * values, and Conversion %.
 *
 * Numbers come from GET /api/admin/sales-stats — the SAME endpoint
 * the Analytics tab reads for its "Salesperson Breakdown" table —
 * so the two screens can never show different numbers for the same
 * period. Query path: dedupe_quotations → collapse_comparison_pairs
 * → aggregate. Missing/null order_status counts as pending
 * (consistent with the rest of the app).
 *
 * Click a Pending / Lost count to expand a per-quotation drill-down
 * row (fetches GET /api/admin/salesperson-drilldown which reuses
 * the identical dedupe+collapse pipeline).
 *
 * Admin/manager only — enforced by /admin/sales-stats +
 * /admin/salesperson-drilldown both using get_admin_user.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { Loader2, Users, ChevronDown, ChevronRight, IndianRupee, Download } from 'lucide-react';
import { toast } from 'sonner';
import { api, todayIst, addDaysIst, istWeekStart, istMonthStart } from '../../lib/store';

const fmtINR = (n) => {
  const v = Math.round(Number(n) || 0);
  return `₹${v.toLocaleString('en-IN')}`;
};
// IST calendar dates (the backend reads every range as IST days).
const isoDaysAgo = (n) => addDaysIst(-n);
const isoToday = () => todayIst();

export default function ReportsPanel() {
  const [rangePreset, setRangePreset] = useState('month');
  const [startDate, setStartDate] = useState(() => istMonthStart());
  const [endDate, setEndDate] = useState(isoToday());
  const [salesperson, setSalesperson] = useState('all');
  const [salespeople, setSalespeople] = useState([]);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});  // key: `${uid}:pending|lost` → { loading, rows }
  // Report download: dropdown chooses which of 4 report
  // types the "Download PDF" button will render.
  const [reportType, setReportType] = useState('pending');
  const [downloading, setDownloading] = useState(false);

  const handleDownloadReport = async () => {
    setDownloading(true);
    try {
      const params = { type: reportType, salesperson };
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const res = await api.downloadReportPdf(params);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const today = todayIst();
      const slug = { pending: 'Pending_Quotations', lost: 'Lost_Quotations',
                     complaints: 'Complaints', leads: 'Leads' }[reportType];
      a.href = url;
      a.download = `HEDA_${slug}_Report_${today}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded');
    } catch (err) {
      // err.response.data is a Blob when responseType is 'blob' — parse it.
      let msg = 'Failed to download report';
      try {
        const blob = err.response?.data;
        if (blob && typeof blob.text === 'function') {
          const raw = await blob.text();
          try { msg = JSON.parse(raw).detail || msg; } catch { msg = raw || msg; }
        } else if (err.response?.data?.detail) {
          msg = err.response.data.detail;
        }
      } catch { /* ignore */ }
      toast.error(msg);
    } finally {
      setDownloading(false);
    }
  };

  const applyPreset = (preset) => {
    setRangePreset(preset);
    if (preset === 'all') { setStartDate(''); setEndDate(''); }
    else if (preset === 'week') {
      setStartDate(istWeekStart());
      setEndDate(isoToday());
    } else if (preset === 'month') {
      setStartDate(istMonthStart());
      setEndDate(isoToday());
    } else if (preset === '30d') {
      setStartDate(isoDaysAgo(29)); setEndDate(isoToday());
    } else if (preset === 'custom' && !startDate && !endDate) {
      setStartDate(isoDaysAgo(29)); setEndDate(isoToday());
    }
  };

  // Fetch salespeople list once (same endpoint the digest tools already use).
  useEffect(() => {
    api.getSalespeople().then(r => setSalespeople(r.data?.salespeople || r.data || [])).catch(() => setSalespeople([]));
  }, []);

  // Fetch sales-stats whenever the range changes. Drill-down cache
  // reset so stale rows don't linger under a different range.
  const fetchRows = useCallback(() => {
    setLoading(true);
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    api.getAdminAnalytics(params)
      .then(r => {
        // Fix: /admin/analytics returns `by_salesperson`
        // (aggregated closed/pending/lost counts + values, dedupe +
        // collapse already applied) — same array the Analytics tab's
        // Salesperson Breakdown table reads. Field names:
        //   name, staff_code, count, value,
        //   closed_count / pending_count / lost_count,
        //   closed_value / pending_value / lost_value.
        const bySales = r.data?.by_salesperson || [];
        setRows(bySales);
        // Analytics endpoint doesn't emit a `summary` block in the
        // same shape sales-stats does; derive a light one for the
        // hint line so numbers match what's rendered.
        const totalCount = bySales.reduce((s, x) => s + (x.count || 0), 0);
        const totalValue = bySales.reduce((s, x) => s + (x.value || 0), 0);
        setSummary({ total_count: totalCount, total_value: totalValue });
        setExpanded({});
      })
      .catch(() => { setRows([]); setSummary(null); })
      .finally(() => setLoading(false));
  }, [startDate, endDate]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const visibleRows = salesperson === 'all'
    ? rows
    : rows.filter(r => r.user_id === salesperson);

  const toggleDrill = async (uid, status) => {
    const key = `${uid}:${status}`;
    if (expanded[key]) {
      setExpanded(prev => { const next = { ...prev }; delete next[key]; return next; });
      return;
    }
    setExpanded(prev => ({ ...prev, [key]: { loading: true, rows: [] } }));
    try {
      const params = { user_id: uid, status };
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const r = await api.getSalespersonDrilldown(params);
      setExpanded(prev => ({ ...prev, [key]: { loading: false, rows: r.data?.quotations || [] } }));
    } catch (e) {
      setExpanded(prev => ({ ...prev, [key]: { loading: false, rows: [], error: true } }));
    }
  };

  return (
    <div className="space-y-4" data-testid="reports-panel">
      <Card className="rounded-2xl border-heda-line bg-white shadow-none">
        <CardHeader className="border-b border-heda-line px-5 py-3">
          <CardTitle className="flex items-center gap-2 font-display text-[15px] font-bold text-heda-ink">
            <Users className="w-4 h-4 text-heda-gold" />
            Salesperson Performance
          </CardTitle>
        </CardHeader>
        <CardContent className="px-5 py-4 space-y-3">
          <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:items-center" data-testid="reports-filter-bar">
            {/* 1. Report Type */}
            <Select value={reportType} onValueChange={setReportType}>
              <SelectTrigger className="w-full sm:w-52 h-10 rounded-xl bg-white border-heda-line" data-testid="reports-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending" data-testid="reports-type-pending">Pending Quotations</SelectItem>
                <SelectItem value="lost" data-testid="reports-type-lost">Lost Quotations</SelectItem>
                <SelectItem value="complaints" data-testid="reports-type-complaints">Complaints</SelectItem>
                <SelectItem value="leads" data-testid="reports-type-leads">Leads</SelectItem>
              </SelectContent>
            </Select>

            {/* 2. Salesperson (existing, unchanged behaviour — just moved) */}
            <Select value={salesperson} onValueChange={setSalesperson}>
              <SelectTrigger className="w-full sm:w-52 h-10 rounded-xl bg-white border-heda-line" data-testid="reports-salesperson-select">
                <SelectValue placeholder="All salespeople" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All salespeople</SelectItem>
                {salespeople.map(s => (
                  <SelectItem key={s.id || s.user_id} value={s.id || s.user_id}>
                    {s.name}{s.staff_code ? ` · ${s.staff_code}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* 3. Date Range — labels map onto applyPreset's existing keys. */}
            <Select value={rangePreset} onValueChange={applyPreset}>
              <SelectTrigger className="w-full sm:w-52 h-10 rounded-xl bg-white border-heda-line" data-testid="reports-daterange-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all"    data-testid="reports-daterange-all">All Time</SelectItem>
                <SelectItem value="week"   data-testid="reports-daterange-week">This Week</SelectItem>
                <SelectItem value="month"  data-testid="reports-daterange-month">This Month</SelectItem>
                <SelectItem value="30d"    data-testid="reports-daterange-30d">Last 30 Days</SelectItem>
                <SelectItem value="custom" data-testid="reports-daterange-custom">Custom Range</SelectItem>
              </SelectContent>
            </Select>

            {/* 4. Download PDF (existing, unchanged behaviour — just moved) */}
            <Button
              type="button"
              onClick={handleDownloadReport}
              disabled={downloading}
              className="rounded-xl bg-heda-olive text-white shadow-none hover:bg-heda-olive-dark h-10 rounded-xl px-5 font-semibold shadow-none"
              data-testid="reports-download-pdf-btn"
            >
              {downloading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />}
              Download PDF
            </Button>
          </div>

          {/* Custom Range inputs — visible ONLY when 'custom' is selected.
              Uses the same startDate/endDate state as before; hidden (not
              disabled) for every other preset, and switching away leaves
              state intact. */}
          {rangePreset === 'custom' && (
            <div className="flex flex-col sm:flex-row gap-2 sm:items-end" data-testid="reports-custom-range-row">
              <div className="flex flex-col gap-1">
                <label className="heda-label !mb-0" htmlFor="reports-from-date">From</label>
                <Input
                  id="reports-from-date"
                  type="date"
                  value={startDate}
                  onChange={(e) => { setStartDate(e.target.value); setRangePreset('custom'); }}
                  className="w-full sm:w-44 h-10 rounded-xl bg-heda-cream/50 border-heda-line"
                  data-testid="reports-from-date"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="heda-label !mb-0" htmlFor="reports-to-date">To</label>
                <Input
                  id="reports-to-date"
                  type="date"
                  value={endDate}
                  onChange={(e) => { setEndDate(e.target.value); setRangePreset('custom'); }}
                  className="w-full sm:w-44 h-10 rounded-xl bg-heda-cream/50 border-heda-line"
                  data-testid="reports-to-date"
                />
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex items-center gap-2 text-sm text-heda-mute py-10 justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-heda-gold" /> Loading…
            </div>
          ) : visibleRows.length === 0 ? (
            <p className="text-sm text-heda-mute py-10 text-center">
              No quotation activity in the selected range.
            </p>
          ) : (
            <div className="-mx-5 border-y border-heda-line overflow-x-auto">
              <table className="w-full text-left text-sm" data-testid="reports-salesperson-table">
                <thead className="heda-thead">
                  <tr>
                    <th className="px-3 sm:px-4 py-2.5 font-semibold">Salesperson</th>
                    <th className="px-3 sm:px-4 py-2.5 font-semibold text-right">Total</th>
                    <th className="px-3 sm:px-4 py-2.5 font-semibold text-right hidden sm:table-cell">Value</th>
                    <th className="px-3 sm:px-4 py-2.5 font-semibold text-right hidden sm:table-cell">Closed</th>
                    <th className="px-3 sm:px-4 py-2.5 font-semibold text-right">Pending</th>
                    <th className="px-3 sm:px-4 py-2.5 font-semibold text-right">Lost</th>
                    <th className="px-3 sm:px-4 py-2.5 font-semibold text-right hidden md:table-cell">Conv. %</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map(r => {
                    const uid = r.user_id;
                    const pKey = `${uid}:pending`;
                    const lKey = `${uid}:lost`;
                    return (
                      <React.Fragment key={uid}>
                        <tr className="border-t border-heda-line hover:bg-heda-cream/50" data-testid={`reports-row-${uid}`}>
                          <td className="px-3 sm:px-4 py-3 font-medium text-heda-ink">
                            {r.name}
                            {r.staff_code && (
                              <span className="ml-2 rounded-full bg-heda-olive-tint px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-heda-olive-dark">{r.staff_code}</span>
                            )}
                          </td>
                          <td className="px-3 sm:px-4 py-3 text-right tabular-nums">{r.count}</td>
                          <td className="px-3 sm:px-4 py-3 text-right tabular-nums font-bold text-heda-ink hidden sm:table-cell">{fmtINR(r.value)}</td>
                          <td className="px-3 sm:px-4 py-3 text-right tabular-nums hidden sm:table-cell">
                            <span className="text-emerald-700 font-semibold">{r.closed_count}</span>
                            <span className="text-heda-mute text-[10.5px] block">{fmtINR(r.closed_value)}</span>
                          </td>
                          <td className="px-3 sm:px-4 py-3 text-right tabular-nums">
                            <button
                              type="button"
                              onClick={() => toggleDrill(uid, 'pending')}
                              className="inline-flex min-h-[32px] items-center gap-1 hover:underline text-amber-700"
                              data-testid={`reports-drill-pending-${uid}`}
                            >
                              {expanded[pKey] ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                              <span className="font-semibold">{r.pending_count}</span>
                            </button>
                            <span className="text-heda-mute text-[10.5px] block">{fmtINR(r.pending_value)}</span>
                          </td>
                          <td className="px-3 sm:px-4 py-3 text-right tabular-nums">
                            <button
                              type="button"
                              onClick={() => toggleDrill(uid, 'lost')}
                              className="inline-flex min-h-[32px] items-center gap-1 hover:underline text-red-700"
                              data-testid={`reports-drill-lost-${uid}`}
                            >
                              {expanded[lKey] ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                              <span className="font-semibold">{r.lost_count}</span>
                            </button>
                            <span className="text-heda-mute text-[10.5px] block">{fmtINR(r.lost_value)}</span>
                          </td>
                          <td className="px-3 sm:px-4 py-3 text-right tabular-nums font-semibold hidden md:table-cell">
                            {r.count > 0 ? `${((r.closed_count / r.count) * 100).toFixed(1)}%` : '—'}
                          </td>
                        </tr>
                        {['pending', 'lost'].map(status => {
                          const key = `${uid}:${status}`;
                          const drill = expanded[key];
                          if (!drill) return null;
                          return (
                            <tr key={key} className="bg-heda-cream/50" data-testid={`reports-drill-panel-${uid}-${status}`}>
                              <td colSpan={7} className="px-3 sm:px-4 py-3">
                                {drill.loading ? (
                                  <div className="text-[12px] text-heda-mute flex items-center gap-1.5">
                                    <Loader2 className="w-3.5 h-3.5 animate-spin text-heda-gold" /> Loading {status}…
                                  </div>
                                ) : drill.rows.length === 0 ? (
                                  <p className="text-[12px] text-heda-mute">No {status} quotations in range.</p>
                                ) : (
                                  <div className="rounded-xl border border-heda-line bg-white overflow-x-auto">
                                    <table className="w-full text-left text-xs">
                                      <thead className="heda-thead">
                                        <tr>
                                          <th className="px-3 py-2 font-semibold">Quote #</th>
                                          <th className="px-3 py-2 font-semibold">Customer</th>
                                          <th className="px-3 py-2 font-semibold hidden sm:table-cell">Created</th>
                                          <th className="px-3 py-2 font-semibold text-right hidden sm:table-cell">Days</th>
                                          <th className="px-3 py-2 font-semibold text-right">Value</th>
                                          {status === 'lost' && <th className="px-3 py-2 font-semibold hidden md:table-cell">Lost reason</th>}
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {drill.rows.map(q => (
                                          <tr key={q.id} className="border-t border-heda-line">
                                            <td className="px-3 py-2 font-mono text-heda-ink">{q.quotation_number || '—'}</td>
                                            <td className="px-3 py-2 truncate max-w-[180px]" title={q.customer_name}>{q.customer_name || '—'}</td>
                                            <td className="px-3 py-2 text-heda-sub hidden sm:table-cell">{q.created_at ? q.created_at.slice(0, 10) : '—'}</td>
                                            <td className="px-3 py-2 text-right hidden sm:table-cell">{q.days_since ?? '—'}</td>
                                            <td className="px-3 py-2 text-right tabular-nums font-semibold text-heda-ink whitespace-nowrap">{fmtINR(q.grand_total)}</td>
                                            {status === 'lost' && (
                                              <td className="px-3 py-2 italic text-heda-mute hidden md:table-cell" title={q.lost_reason || ''}>
                                                {q.lost_reason || '—'}
                                              </td>
                                            )}
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {summary && (
            <p className="text-[11.5px] text-heda-mute pt-1" data-testid="reports-summary-hint">
              Range totals — {summary.total_count} quotations, {fmtINR(summary.total_value)}. Numbers reuse the
              Analytics tab&apos;s dedupe + comparison-collapse pipeline; Conversion % = closed ÷ total.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
